/* eslint-disable @typescript-eslint/no-explicit-any */
// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { GridColDef, GridRowParams } from '@mui/x-data-grid';
import { AsyncButton } from '../components/Button/AsyncButton';
import { SelectInput } from '../components/Form/SelectInput';
import Loading from '../components/Loading';
import { DataTable } from '../components/Table/DataTable';
import { useUtilityAutomationClient } from '../contexts/UtilityAutomationApiContext';
import useTasks from '../hooks/queries/deadtasks/useTasks';
import useTriggerNames from '../hooks/queries/deadtasks/useTriggerNames';
import { DeadTask, UtilityBackend } from '../utils/UtilityAutomationApiClient';
import { deepExactSearch } from '../utils/deepExtractSearch';
import { PageWrapper } from './PageWrapper';

const kebabToTitleCase = (source: string): string =>
  source
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
interface CopyTextBoxProps {
  text: string;
  enableMono?: boolean;
}
const SimpleCopyText: React.FC<CopyTextBoxProps> = ({ text, enableMono }) => {
  const { enqueueSnackbar } = useSnackbar();

  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(text)
      .then(() => enqueueSnackbar('Successfully copied to clipboard', { variant: 'success' }))
      .catch(() => enqueueSnackbar('Failed to copy to clipboard', { variant: 'error' }));
  };
  return (
    <Typography
      sx={{ cursor: 'pointer', fontFamily: enableMono ? 'monospace' : undefined }}
      variant="caption"
      onClick={copyToClipboard}
    >
      {text}
    </Typography>
  );
};

const TaskDetails = ({
  open,
  task,
  onClose,
  onDelete,
}: {
  open: boolean;
  onClose: () => void;
  task?: DeadTask<any>;
  onDelete: () => Promise<void>;
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const prettyJson = JSON.stringify(task, null, 2);
  if (!task) {
    return null;
  }
  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(prettyJson)
      .then(() => enqueueSnackbar('Successfully copied to clipboard', { variant: 'success' }))
      .catch(() => enqueueSnackbar('Failed to copy to clipboard', { variant: 'error' }));
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle sx={{ display: 'flex', flexD: 'row', p: 2 }}>
        <Typography variant="h4">Task details</Typography>
        <Box sx={{ ml: 'auto' }}>
          <IconButton onClick={onClose} size="small">
            <Close color="action" fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <Box sx={{ overflowWrap: 'break-word', p: 1 }}>
        <Typography variant="body2">Task ID</Typography>
        <SimpleCopyText enableMono text={task.id} />
      </Box>
      <Button onClick={copyToClipboard} sx={{ m: 1 }} variant="outlined" size="small">
        Copy below
      </Button>
      <pre
        style={{
          backgroundColor: '#f4f4f4',
          padding: '10px',
          paddingTop: 0,
          borderRadius: '5px',
          width: '100%',
          overflowX: 'auto', // Keep this for cases where a very long single line might still occur
          fontFamily: 'monospace', // Ensure monospaced font for alignment
          whiteSpace: 'pre-wrap', // *** KEY CSS PROPERTY FOR WRAPPING ***
          wordBreak: 'break-word', // *** HELPS BREAK LONG WORDS/STRINGS ***
        }}
      >
        <code>{prettyJson}</code>
      </pre>

      <DialogActions sx={{ ml: 'auto', p: 1 }}>
        <AsyncButton variant="outlined" color="error" onClick={onDelete}>
          Delete
        </AsyncButton>
      </DialogActions>
    </Dialog>
  );
};

const Tasks = ({ backend, trigger }: { backend: UtilityBackend; trigger?: string }) => {
  const tasks = useTasks(backend, trigger);
  const utilityAutomationClient = useUtilityAutomationClient();
  const [selectedTask, setSelectedTask] = useState<DeadTask<any>>();
  const [search, setSearch] = useState<string>('');
  const { enqueueSnackbar } = useSnackbar();

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };
  const onClick = (r: GridRowParams<DeadTask<any>>) => {
    setSelectedTask(r.row);
  };
  const onClose = () => {
    setSelectedTask(undefined);
  };

  const filteredTasks = (tasks.data || []).filter((task) => deepExactSearch(task, search));
  if (!trigger) {
    return <p>Select a trigger to view its dead tasks.</p>;
  }
  const onDelete = async (taskId?: string) => {
    if (!taskId) {
      throw new Error('No task id');
    }
    if (!trigger) {
      throw new Error('no trigger selected');
    }

    await utilityAutomationClient.deleteTask(backend, trigger, taskId);

    setSelectedTask(undefined);
  };

  const onBulkDelete = async () => {
    const promises = filteredTasks.map((task) => onDelete(task.id));
    await Promise.all(promises);
  };
  const onBulkCopyIds = async () => {
    const taskIds = filteredTasks.map((task) => task.id);
    try {
      await navigator.clipboard.writeText(JSON.stringify(taskIds, null, 2));
      enqueueSnackbar('Successfully copied to clipboard', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar('Failed to copy to clipboard', { variant: 'error' });
    }
  };
  const onBulkCopyJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(filteredTasks, null, 2));
      enqueueSnackbar('Successfully copied to clipboard', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar('Failed to copy to clipboard', { variant: 'error' });
    }
  };
  const displayButtons = filteredTasks.length > 0;
  const headers: GridColDef<DeadTask<any>>[] = [
    {
      field: 'id',
      headerName: 'Task ID',
      flex: 30,
      filterable: true,
      valueGetter: ({ row: task }) => task.id,
      renderCell: ({ row: task }) => (
        <Tooltip sx={{ cursor: 'pointer' }} title="Click to copy">
          <Box
            sx={{
              cursor: 'pointer',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <SimpleCopyText enableMono text={task.id} />
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'reason',
      headerName: 'Reason',
      renderCell: ({ row: task }) => (
        <Typography sx={{ cursor: 'pointer' }} variant="body2" noWrap>
          {task.reason}
        </Typography>
      ),
      flex: 100,
      valueGetter: ({ row: task }) => task.reason,
      filterable: true,
    },
  ];
  return (
    <PageWrapper pageName={`Dead tasks for ${kebabToTitleCase(trigger)}`}>
      <TaskDetails
        open={!!selectedTask}
        onClose={onClose}
        task={selectedTask}
        onDelete={() => onDelete(selectedTask?.id)}
      />
      <TextField
        sx={{ mt: 1 }}
        size="small"
        fullWidth
        placeholder="Search"
        onChange={handleInputChange}
        value={search}
      />
      <Box
        sx={{
          mt: 1,
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <AsyncButton
          sx={{ mr: 1 }}
          onClick={onBulkDelete}
          color="error"
          variant="outlined"
          size="small"
          disabled={!displayButtons}
        >
          Delete all dead tasks {`(${filteredTasks.length})`}
        </AsyncButton>

        <AsyncButton
          disabled={!displayButtons}
          sx={{ mr: 1 }}
          onClick={onBulkCopyIds}
          variant="outlined"
          size="small"
        >
          Copy all task Ids {`(${filteredTasks.length})`}
        </AsyncButton>
        <AsyncButton
          disabled={!displayButtons}
          onClick={onBulkCopyJson}
          variant="outlined"
          size="small"
        >
          Copy all JSON objects {`(${filteredTasks.length})`}
        </AsyncButton>
      </Box>
      <DataTable
        onRowClick={onClick}
        columns={headers}
        rows={filteredTasks}
        rowKey={(task) => task.id}
      />
    </PageWrapper>
  );
};

export const DeadTasks = ({ backend }: { backend: UtilityBackend }) => {
  const triggerNames = useTriggerNames(backend);
  const [selectedTrigger, setSelectedTrigger] = useState<string>();

  if (triggerNames.isLoading) {
    return (
      <>
        <p>Loading triggers...</p>
        <Loading />
      </>
    );
  }
  if (triggerNames.isError) {
    console.error('Error loading triggers:', triggerNames.error);
    return <p>Could not load triggers.</p>;
  }

  return (
    <>
      <SelectInput
        label="Select trigger"
        value={selectedTrigger ?? ''}
        setValue={setSelectedTrigger}
        values={triggerNames.data.map((trigger) => ({
          value: trigger,
          display: kebabToTitleCase(trigger),
        }))}
      />
      <Tasks backend={backend} trigger={selectedTrigger} />
    </>
  );
};
